import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPenNib,
  faCopy,
  faDownload,
  faTrash,
  faRightToBracket,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";

import { useT } from "../../i18n/I18nContext";
import ConfirmDialog from "../Shared/ConfirmDialog";

const STORAGE_TITLE = "writeon:basic_editor:title";
const STORAGE_BODY = "writeon:basic_editor:body";

// A genuinely useful editor for visitors who don't (yet) have an
// account. Writes locally to ``localStorage`` so the draft survives
// a refresh, the navbar, even closing the tab — but never touches
// the backend, so there's no auth requirement, no presigned-URL
// dance, and no "log in to keep going" wall blocking the actual
// writing. Sign-up is offered at the bottom for visitors who want
// to upgrade their draft into a real project.
export default function BasicEditor() {
  const t = useT();
  const [title, setTitle] = useState(() => {
    if (typeof window === "undefined") return "";
    try {
      return window.localStorage.getItem(STORAGE_TITLE) || "";
    } catch {
      return "";
    }
  });
  const [body, setBody] = useState(() => {
    if (typeof window === "undefined") return "";
    try {
      return window.localStorage.getItem(STORAGE_BODY) || "";
    } catch {
      return "";
    }
  });
  const [savedAt, setSavedAt] = useState(null);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const saveTimer = useRef(null);

  // Debounced auto-save. Writing fires on every keystroke; flushing
  // to localStorage on every keystroke is fine but spams the
  // "Saved · just now" indicator, so coalesce to ~400 ms idle.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      try {
        window.localStorage.setItem(STORAGE_TITLE, title);
        window.localStorage.setItem(STORAGE_BODY, body);
        setSavedAt(Date.now());
      } catch {
        /* private mode / quota — ignore, the editor still works in-memory */
      }
    }, 400);
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [title, body]);

  const wordCount = body.trim().split(/\s+/).filter(Boolean).length;
  const charCount = body.length;
  const readingMinutes = Math.max(1, Math.round(wordCount / 200));

  const handleCopy = async () => {
    const out = title ? `${title}\n\n${body}` : body;
    // ``navigator.clipboard.writeText`` requires a secure context
    // (HTTPS or localhost) and is unavailable on iOS Safari < 13.1
    // and a few corporate-managed browsers. The execCommand path
    // is deprecated but still ships in every browser we support
    // and works in any context, so use it as the fallback. Both
    // can throw if the page lost user-activation, hence the outer
    // try/catch around the legacy path too.
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(out);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = out;
        // ``readOnly`` + off-screen positioning prevents iOS from
        // zooming into the textarea before the copy fires.
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(textarea);
        if (!ok) throw new Error("execCommand_copy_failed");
      }
      toast.success(t("editor.basic.toast.copied"));
    } catch {
      toast.error(t("editor.basic.toast.copy_failed"));
    }
  };

  const handleDownload = () => {
    const out = title ? `${title}\n\n${body}` : body;
    const blob = new Blob([out], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    const safeTitle = (title || "draft")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")
      .slice(0, 40) || "draft";
    a.href = url;
    a.download = `plumescript-${safeTitle}-${stamp}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleClear = () => {
    if (!body && !title) return;
    setConfirmClearOpen(true);
  };

  const confirmedClear = () => {
    setTitle("");
    setBody("");
    try {
      window.localStorage.removeItem(STORAGE_TITLE);
      window.localStorage.removeItem(STORAGE_BODY);
    } catch {
      /* ignore */
    }
    toast.success(t("editor.basic.toast.cleared"));
    setConfirmClearOpen(false);
  };

  return (
    <div className="bg-bg min-h-[calc(100vh-72px)]">
      <div className="max-w-4xl mx-auto px-5 py-10 lg:py-14">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <span className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-primaryMuted text-primary">
            <FontAwesomeIcon icon={faPenNib} />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl md:text-2xl font-bold text-text">
              {t("editor.basic.title")}
            </h1>
            <p className="text-sm text-secondText">
              {t("editor.basic.subtitle")}
            </p>
          </div>
          {savedAt ? (
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-secondText">
              <FontAwesomeIcon icon={faCircleCheck} className="text-success" />
              {t("editor.basic.saved_locally")}
            </span>
          ) : null}
        </div>

        {/* Editor surface */}
        <div className="rounded-2xl border border-border bg-surface shadow-sm overflow-hidden">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("editor.basic.title_placeholder")}
            aria-label={t("editor.basic.title_placeholder")}
            className="block w-full bg-transparent border-0 px-5 lg:px-8 py-4 text-2xl md:text-3xl font-bold text-text placeholder:text-secondText/70 focus:outline-none border-b border-border"
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t("editor.basic.body_placeholder")}
            aria-label={t("editor.basic.body_placeholder")}
            rows={18}
            className="block w-full bg-transparent border-0 resize-y px-5 lg:px-8 py-5 text-base lg:text-lg leading-relaxed text-text placeholder:text-secondText/70 focus:outline-none min-h-[24rem]"
          />
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-surfaceAlt/50 px-5 lg:px-8 py-3">
            <div className="text-xs sm:text-sm text-secondText space-x-3">
              <span>
                <span className="font-semibold text-text">{wordCount}</span>{" "}
                {t("editor.basic.stats.words")}
              </span>
              <span aria-hidden="true">·</span>
              <span>
                <span className="font-semibold text-text">{charCount}</span>{" "}
                {t("editor.basic.stats.chars")}
              </span>
              <span aria-hidden="true">·</span>
              <span>
                {t("editor.basic.stats.reading", { minutes: readingMinutes })}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1.5 text-sm text-text hover:border-primary hover:text-primary transition focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <FontAwesomeIcon icon={faCopy} className="text-xs" />
                {t("editor.basic.action.copy")}
              </button>
              <button
                type="button"
                onClick={handleDownload}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1.5 text-sm text-text hover:border-primary hover:text-primary transition focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <FontAwesomeIcon icon={faDownload} className="text-xs" />
                {t("editor.basic.action.download")}
              </button>
              <button
                type="button"
                onClick={handleClear}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1.5 text-sm text-secondText hover:border-danger hover:text-danger transition focus:outline-none focus:ring-2 focus:ring-danger/30"
              >
                <FontAwesomeIcon icon={faTrash} className="text-xs" />
                {t("editor.basic.action.clear")}
              </button>
            </div>
          </div>
        </div>

        {/* Sign-up upgrade footer — soft pitch, doesn't block. */}
        <div className="mt-8 rounded-2xl border border-dashed border-border bg-surfaceAlt/40 p-6 flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
          <div className="min-w-0 flex-1 text-center sm:text-left">
            <h3 className="text-base font-semibold text-text mb-1">
              {t("editor.basic.upgrade.title")}
            </h3>
            <p className="text-sm text-secondText">
              {t("editor.basic.upgrade.description")}
            </p>
          </div>
          <Link
            to="/register"
            className="shrink-0 inline-flex items-center gap-2 rounded-full bg-primary text-white px-5 py-2.5 text-sm font-semibold hover:brightness-110 transition focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            <FontAwesomeIcon icon={faRightToBracket} />
            {t("editor.basic.upgrade.cta")}
          </Link>
        </div>
      </div>

      <ConfirmDialog
        open={confirmClearOpen}
        title={t("editor.basic.confirm_clear_title")}
        message={t("editor.basic.confirm_clear")}
        confirmLabel={t("editor.basic.confirm_clear_action")}
        cancelLabel={t("work.modal.cancel")}
        tone="danger"
        onConfirm={confirmedClear}
        onCancel={() => setConfirmClearOpen(false)}
      />
    </div>
  );
}
